import { useEffect, useRef, useState } from 'react';
import { Grid, IconButton, useTheme, Modal, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { ActionCard, AppText, Chip, IconBox } from '@zippeditoolsjs/ui-web';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import ReactCodeInput from 'react-code-input';
import {
  getAuth,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';

import StyledModalBox from '../../tools/StyledModalBox';
import BackLink from '../../ui/controls/BackLink';
import { maskPhoneNumber } from '../../utils/phone';
import {
  buildTotpAssertion,
  getPhoneHint,
  getTotpHint,
} from '../../login/policy/totpChallenge';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';

const STEPS = { CHOOSE: 'choose', SMS: 'sms', TOTP: 'totp' };
const GENERIC_ERROR = 'heimdal_app.Login.try_again_later';

/**
 * Re-authentication challenge for the profile. Answers whichever second factor
 * the account carries, and lets an account holding both pick.
 *
 * `onVerificationComplete` may be async and may report a failure as
 * `{ ok: false, errorKey }`: passing the challenge only unlocks the operation
 * the caller still has to perform, so the dialog stays open until it lands.
 */
export default function MFAVerify(props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    open,
    setOpen,
    userProfile,
    resolver,
    setIsLoading,
    onVerificationComplete,
  } = props;
  const [code, setCode] = useState('');
  const [step, setStep] = useState(null);
  const [verificationId, setVerificationId] = useState('');
  const [countdown, setCountdown] = useState(15);
  const [language, setLanguage] = useState('en');
  const [showButton, setShowButton] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  // The resolver is spent by the first resolveSignIn that succeeds; what a retry
  // has left to repeat is the operation, on the credential it handed back.
  const [passedCredential, setPassedCredential] = useState(null);
  // A double tap lands in the same frame, before any state has committed, and
  // the credential can only be spent once, so the guard has to be a ref.
  const verifyInFlight = useRef(false);
  const { inputBackground = 'white' } = props;
  const lightBackground = inputBackground
    ? inputBackground
    : Object.values(theme.palette[inputBackground])[0];

  // Read from the resolver rather than assumed: `hints[0]` is whatever order
  // the SDK happened to return, which left TOTP-only accounts stuck.
  const totpHint = getTotpHint(resolver);
  const phoneHint = getPhoneHint(resolver);
  const canChoose = Boolean(totpHint && phoneHint);

  const styles = {
    codeInput: {
      fontFamily: 'monospace',
      MozAppearance: 'textfield',
      borderRadius: '6px',
      border: '1px solid',
      boxShadow: '0px 0px 10px 0px rgba(0,0,0,.10)',
      margin: '4px',
      paddingLeft: '8px',
      width: '36px',
      height: '42px',
      fontSize: '32px',
      boxSizing: 'border-box',
      color: theme.palette.type === 'light' ? 'black' : 'white',
      backgroundColor:
        theme.palette.type === 'light'
          ? lightBackground
          : theme.palette.paper.main,
      borderColor: 'lightgrey',
    },
    loadingButton: !showButton
      ? {
          color:
            theme.palette.type === 'light'
              ? 'rgba(0, 0, 0, 0.13)'
              : 'rgba(255, 255, 255, 0.13)',
        }
      : {
          color: theme.palette.type === 'light' ? 'black' : 'white',
        },
  };

  /** Answers the challenge, once, and hands back the credential it produced. */
  const resolveChallenge = async () => {
    let multiFactorAssertion;
    try {
      multiFactorAssertion =
        step === STEPS.TOTP
          ? buildTotpAssertion(resolver, code)
          : PhoneMultiFactorGenerator.assertion(
              PhoneAuthProvider.credential(verificationId, code),
            );
    } catch (error) {
      // A missing hint or verification id, not a code the user got wrong:
      // saying "wrong code" here would misdirect them.
      console.error('MFAVerify.handleVerify:', getSafeErrorIdentifier(error));
      return { errorKey: GENERIC_ERROR };
    }

    try {
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
      return { userCredential };
    } catch (error) {
      // A wrong code used to fail into a silent console line, leaving the
      // dialog looking unresponsive with no way to tell what went wrong.
      console.error('MFAVerify.handleVerify:', getSafeErrorIdentifier(error));
      return {
        errorKey:
          error?.code === 'auth/invalid-verification-code'
            ? 'heimdal_app.Login.wrong_code'
            : GENERIC_ERROR,
      };
    }
  };

  const handleVerify = async () => {
    if (!resolver || verifyInFlight.current) return;
    // The code only gates the challenge; past it the code is spent along with
    // the resolver, and Confirm retries the operation instead.
    if (!passedCredential && code.length < 6) return;
    verifyInFlight.current = true;
    setIsVerifying(true);
    setErrorKey(null);

    let userCredential = passedCredential;
    if (!userCredential) {
      const challenge = await resolveChallenge();
      if (!challenge.userCredential) {
        verifyInFlight.current = false;
        setIsVerifying(false);
        setErrorKey(challenge.errorKey);
        return;
      }
      userCredential = challenge.userCredential;
      // Firebase rejects a second resolveSignIn on the same resolver, so a retry
      // past this point may only repeat what the credential unlocked.
      setPassedCredential(userCredential);
    }

    // Stay busy until the operation the challenge unlocked lands, or a failed
    // save would close the dialog looking like a success.
    let result;
    try {
      result = await onVerificationComplete(userCredential);
    } catch (error) {
      result = { ok: false };
      console.error('MFAVerify.onComplete:', getSafeErrorIdentifier(error));
    }
    if (result && result.ok === false) {
      verifyInFlight.current = false;
      setIsVerifying(false);
      setErrorKey(result.errorKey || GENERIC_ERROR);
      return;
    }
    setIsVerifying(false);
    setOpen(false);
  };

  const sentSMS = () => {
    const auth = getAuth();
    auth.tenantId = userProfile.tenantId;
    auth.languageCode = language;
    const phoneInfoOptions = {
      multiFactorHint: phoneHint,
      session: resolver.session,
    };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    return phoneAuthProvider
      .verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier)
      .then(function (verificationId) {
        setVerificationId(verificationId);
        setIsLoading(false);
      })
      .catch(function (error) {
        // Releasing the spinner matters as much as reporting: the parent raised
        // it before re-authenticating and has no other way to hear about this.
        setIsLoading(false);
        setErrorKey(GENERIC_ERROR);
        console.error('MFAVerify.sentSMS:', getSafeErrorIdentifier(error));
      });
  };

  const restartTimer = () => {
    setShowButton(false);
    setCountdown(15);
    sentSMS();
  };

  const goToStep = nextStep => {
    setCode('');
    setErrorKey(null);
    setStep(nextStep);
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (!open || !resolver) {
      setStep(null);
      return;
    }
    setCode('');
    setErrorKey(null);
    setPassedCredential(null);
    verifyInFlight.current = false;
    // Nothing to choose on a single factor; TOTP is the default when neither
    // hint is one we understand.
    setStep(canChoose ? STEPS.CHOOSE : phoneHint ? STEPS.SMS : STEPS.TOTP);
  }, [open, resolver]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === STEPS.SMS) {
      restartTimer();
      return;
    }
    // The chooser and the authenticator app have nothing to wait for, so the
    // spinner the parent raised has to come down here instead.
    if (step) setIsLoading(false);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== STEPS.SMS) return;
    if (countdown <= 0) {
      setShowButton(true);
      return;
    }
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown, step]);

  useEffect(() => {
    let languages = ['en', 'es', 'de'];
    let userLanguage = navigator.language || navigator.userLanguage || 'en-US';
    userLanguage = userLanguage.split('-')[0];
    userLanguage = languages.includes(userLanguage) ? userLanguage : 'en';

    setLanguage(userLanguage);
  }, []);

  useEffect(() => {
    if (!open && window.recaptchaVerifier) {
      // clear() only flips an internal flag: the instance stays truthy and every
      // later use of it — a second clear() included — throws auth/internal-error.
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  }, [open]);

  const renderChooser = () => (
    <div className="flex flex-col gap-2.5">
      <ActionCard
        startAdornment={
          <IconBox size="sm" tone="accent">
            <IconComponent
              iconName="shield-checkmark"
              style={{ fontSize: 18 }}
            />
          </IconBox>
        }
        title={t('heimdal_app.TOTP.authenticator_app')}
        hint={t('heimdal_app.TOTP.method_totp_description')}
        titleAdornment={
          <Chip tone="success">{t('heimdal_app.TOTP.recommended')}</Chip>
        }
        onClick={() => goToStep(STEPS.TOTP)}
      />
      <ActionCard
        startAdornment={
          <IconBox size="sm" tone="muted">
            <IconComponent
              iconName="chatbubble-ellipses-outline"
              style={{ fontSize: 18 }}
            />
          </IconBox>
        }
        title={t('heimdal_app.TOTP.method_sms_title', {
          phone: maskPhoneNumber(phoneHint?.phoneNumber),
        })}
        hint={t('heimdal_app.TOTP.method_sms_description')}
        onClick={() => goToStep(STEPS.SMS)}
      />
    </div>
  );

  // Past the challenge nothing about the code matters any more: another method,
  // another SMS and another code all lead back to a resolver that is spent.
  const renderCodeStep = () => (
    <>
      {canChoose && !passedCredential ? (
        <BackLink
          onClick={() => goToStep(STEPS.CHOOSE)}
          label={t('heimdal_app.OTP.use_another_method')}
          disabled={isVerifying}
        />
      ) : null}
      <Typography
        id="modal-modal-description"
        variant="body2"
        textAlign={'left'}>
        {step === STEPS.TOTP
          ? t('heimdal_app.TOTP.enter_code')
          : t('heimdal_app.Profile.enter_the_6_digit_code')}
      </Typography>
      <Grid
        item
        xs={12}
        sx={{
          marginX: '2',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '1rem',
        }}>
        <ReactCodeInput
          type="tel"
          fields={6}
          onChange={e => setCode(e)}
          value={code}
          disabled={Boolean(passedCredential)}
          inputStyle={styles.codeInput}
        />
      </Grid>
      {step === STEPS.SMS && !passedCredential ? (
        <Grid item xs={12}>
          <LoadingButton
            loading={isVerifying}
            onClick={restartTimer}
            size="small"
            style={styles.loadingButton}
            endIcon={<IconComponent iconName={'send-outline'} />}
            disabled={!showButton || isVerifying}>
            {showButton
              ? t('heimdal_app.OTP.send_sms_again')
              : `${t('heimdal_app.OTP.send_sms_again')} ${countdown}`}
          </LoadingButton>
        </Grid>
      ) : null}
    </>
  );

  return (
    <div>
      <Modal
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
        open={open}
        onClose={() => {
          // Backdrop and Escape are the third way out of this dialog; the
          // close button and Cancel are already held shut while one lands.
          if (isVerifying) return;
          handleClose();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <StyledModalBox>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} md={12}>
              <Grid container>
                <Grid item xs={11}>
                  <Typography
                    id="modal-modal-title"
                    variant="h6"
                    textAlign={'left'}>
                    {step === STEPS.CHOOSE
                      ? t('heimdal_app.TOTP.choose_method_title')
                      : t('heimdal_app.Profile.multi_factor_authentication')}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    aria-label="close"
                    disabled={isVerifying}
                    sx={{
                      position: 'absolute',
                      right: '0.5rem',
                      top: '0.5rem',
                      color: 'text.secondary',
                    }}
                    onClick={() => {
                      handleClose();
                    }}>
                    <IconComponent
                      iconName={'close-outline'}
                      className={'icon'}
                    />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} paddingY={2}>
              {step === STEPS.CHOOSE ? renderChooser() : renderCodeStep()}
              {/* MUI's modal manager aria-hides the app root, so the toast
                  region out there is unreachable while this dialog is open. */}
              {errorKey ? (
                <AppText
                  role="alert"
                  as="p"
                  variant="bodySmall"
                  className="mt-4 text-left text-error-strong">
                  {t(errorKey)}
                </AppText>
              ) : null}
            </Grid>
            <Grid container justifyContent="space-between" display={'flex'}>
              <Grid item xs={3}>
                <Typography
                  variant="subtitle2"
                  textAlign={'left'}
                  color={theme.palette.secondary.main}
                  onClick={() => {
                    // The close button is already disabled while a verification
                    // is landing; this one dismissed the dialog out from under it.
                    if (isVerifying) return;
                    handleClose();
                  }}
                  style={{
                    cursor: isVerifying ? 'default' : 'pointer',
                    userSelect: 'none',
                  }}>
                  {t('heimdal_app.Profile.cancel')}
                </Typography>
              </Grid>
              {step === STEPS.CHOOSE ? null : (
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle2"
                    textAlign={'right'}
                    onClick={handleVerify}
                    style={{ cursor: 'pointer', userSelect: 'none' }}>
                    {t('heimdal_app.Profile.confirm')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </StyledModalBox>
      </Modal>
    </div>
  );
}
